import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { VisitorInquiryDetailDialog } from "@project/admin/components/visitor-detail-dialog";
import { VisitorFormFields } from "@project/admin/components/visitor-form-fields";
import type { Field } from "@project/admin/components/resource-table";
import { projectToast } from "@project/lib/toast";
import { visitorInquiryService } from "@project/services/admin/visitor-inquiry.service";
import {
  buildVisitorInquiryPayload,
  mapVisitorInquiryToForm,
  visitorInquiryDefaultValues,
  type VisitorInquiryPayload,
  type VisitorInquiryRecord,
} from "@project/types/visitor-inquiry";

const tableFields: Field[] = [
  {
    name: "visitor_name",
    label: "Visitor Name",
    showInTable: true,
    hidden: true,
  },
  {
    name: "mobile_number",
    label: "Mobile",
    showInTable: true,
    hidden: true,
  },
  {
    name: "visit_date",
    label: "Visit Date",
    type: "date",
    showInTable: true,
    hidden: true,
  },
  {
    name: "reason_for_visit",
    label: "Reason",
    showInTable: true,
    hidden: true,
  },
  {
    name: "occupation",
    label: "Occupation",
    showInTable: true,
    hidden: true,
  },
  {
    name: "email",
    label: "Email",
    showInTable: true,
    hidden: true,
  },
  {
    name: "reference_source",
    label: "Reference",
    showInTable: true,
    hidden: true,
  },
];

export default function Page() {
  const [viewTarget, setViewTarget] = useState<VisitorInquiryRecord | null>(
    null
  );

  return (
    <AdminShell
      title="Visitor Inquiry"
      subtitle="Planned visit inquiries from the public form"
    >
      <ApiResourceTable<VisitorInquiryRecord>
        title="Visitor Inquiry"
        labelField="visitor_name"
        fields={tableFields}
        searchFields={[
          "visitor_name",
          "mobile_number",
          "email",
          "reason_for_visit",
          "occupation",
          "referrer_name",
        ]}
        defaultValues={
          visitorInquiryDefaultValues() as unknown as Partial<VisitorInquiryRecord>
        }
        enableCsvExport
        csvFileName="visitor-inquiries"
        dialogClassName="max-w-3xl"
        addButtonLabel="Add Inquiry"
        list={query => visitorInquiryService.listInquiriesPage(query)}
        loadRecordForEdit={id => visitorInquiryService.getInquiry(id)}
        create={payload =>
          visitorInquiryService.createInquiry(
            payload as unknown as VisitorInquiryPayload
          )
        }
        update={(id, payload) =>
          visitorInquiryService.updateInquiry(
            id,
            payload as unknown as Partial<VisitorInquiryPayload>
          )
        }
        remove={id => visitorInquiryService.deleteInquiry(id)}
        onView={row => setViewTarget(row)}
        renderForm={({ editing, setEditing }) => (
          <VisitorFormFields
            editing={editing as unknown as Record<string, unknown> | null}
            setEditing={setEditing as never}
            variant="inquiry"
          />
        )}
        buildPayload={editing => {
          if (!editing) {
            return null;
          }
          try {
            return buildVisitorInquiryPayload(
              editing as unknown as Record<string, unknown>
            ) as unknown as Record<string, unknown>;
          } catch (error) {
            projectToast.error(
              error instanceof Error
                ? error.message
                : "Invalid visitor inquiry form"
            );
            return null;
          }
        }}
        mapRecordToForm={record =>
          mapVisitorInquiryToForm(
            record
          ) as unknown as Partial<VisitorInquiryRecord>
        }
      />

      <VisitorInquiryDetailDialog
        open={!!viewTarget}
        onOpenChange={open => {
          if (!open) {
            setViewTarget(null);
          }
        }}
        inquiryId={viewTarget?.id ?? null}
        preview={viewTarget}
      />
    </AdminShell>
  );
}
